import { useState } from 'react'
import { PERMISSIONS, PERMISSION_DENIED_HINT } from '@/lib/permissions'
import { DisabledHint } from '@/components/ui/disabled-hint'
import { Link } from 'react-router-dom'
import { Button } from '../../../ui/button'
import { formatDate } from '../../../../lib/format'
import { isWorkItemSettled } from '../planning-helpers'
import type { WorkItemStatus } from '../../../../lib/api/v2/planning'

/**
 * Report display + missing-report state for a work item's section (SOLY-PLAN-001 MS-27,
 * design Round 4 §Rapport — texte seul V1, pas de 4ème section).
 *
 * Three mutually exclusive renderings inside the Métré / Pose section:
 *  - **Completed** → an extract of `reportSummary` (first line, truncated) + « Voir le rapport »
 *    that expands the full text + « Mis à jour le {date} » + a disabled « Documents — bientôt »
 *    slot (future attachments placeholder, NOT built here), and — SOLY-PLAN-006 MS-07 — a
 *    « Compléter le rapport » gesture, because a métré can be visited again after its report.
 *  - **Missing** (event end past · `reportSummary` null · status still open) → an amber, sober
 *    warning with a « Rédiger le rapport » CTA opening the existing MS-24 ReportForm.
 *  - **Nothing** otherwise (the section keeps its calm factual line).
 *
 * Saisie itself stays in MS-24 (`ReportForm`): this component only DISPLAYS and routes the CTA to
 * the same form via `onOpenReport`.
 */

const EXTRACT_LENGTH = 120

function extract(text: string): string {
  const oneLine = text.replace(/\s+/g, ' ').trim()
  return oneLine.length > EXTRACT_LENGTH ? `${oneLine.slice(0, EXTRACT_LENGTH).trimEnd()}…` : oneLine
}

/**
 * SOLY-PLAN-006 MS-07 — « Compléter le rapport », and where its condition comes from.
 *
 * The gesture lives in the READ view of an existing report, unconditionally. It does NOT re-derive
 * « is this passage covered ? »: that question belongs to the M6 alert (MS-03), which already
 * brings the « Rapport manquant » block back when a créneau has gone by uncovered. Writing the
 * coverage rule a second time on the front is exactly the failure mode `isConfirmationLive`
 * documents in its own comment — a rule written in several places « agrees by review, not by
 * construction » — and that rule already has three homes.
 *
 * `ReportForm` is then opened PRE-FILLED by the caller, which is what stops the save from erasing
 * the first passage's text.
 */
export function ReportArea({
  area, status, reportSummary, reportSummaryAt, eventPast, onOpenReport, eventDateLabel, unreceivedLines,
  reportMissingSignalled = false, readOnly = false,
}: {
  area: 'metre' | 'pose'
  status: WorkItemStatus
  reportSummary: string | null
  reportSummaryAt: string | null
  /** True once the work item's RDV end is strictly in the past (gates the missing-report state). */
  eventPast: boolean
  /**
   * SOLY-PLAN-006 — « un passage est écoulé sans rapport », as decided by the M6 ALERT, not by a
   * coverage rule recomputed here.
   *
   * This is the AC-5 discipline of the mini-scope, and it is not pedantry: `eventPast` above reads
   * the LAST créneau, so on a métré passed Monday and returning Thursday it is false and the amber
   * block never appears — while the Suivi board, which judges every PAST passage, shouts « le métré
   * du 12/10 est passé sans rapport saisi ». The drawer the board links to would show a calm
   * section with no CTA. Re-deriving coverage on the front would be the FOURTH writing of a rule
   * that already has three homes, which is the failure mode `isConfirmationLive` documents.
   */
  reportMissingSignalled?: boolean
  /** Opens the existing MS-24 ReportForm (« Terminer + rapport » intent). */
  onOpenReport: () => void
  /** SOLY-PERM-001 — sans `planning:manage` le rapport se lit mais ne s'écrit pas : le CTA de
   *  clôture devient un simple constat. Le bloc ambre reste, il porte l'information. */
  readOnly?: boolean
  /** « le 16/06 » date label woven into the missing-report copy when known (mini-scope 29). */
  eventDateLabel?: string
  /** SOLY-FINITION-001 — supplier lines still expected at pose closure: a non-blocking reconciliation
   *  reminder with a link to the registre. Never gates the report submit. */
  unreceivedLines?: { count: number; href: string }
}) {
  const [open, setOpen] = useState(false)

  // Same predicate as the agenda bar and both drawer sections (SOLY-PLAN-006 MS-04). For the pose
  // it is byte-identical to the three-terminal-state test it replaces; for the métré a recorded
  // report no longer closes the section, so the amber block can legitimately come back.
  const isClosed = isWorkItemSettled(area === 'metre' ? 'TechnicalSurvey' : 'Installation', status)
  const showMissing = !isClosed && (reportMissingSignalled || (eventPast && !reportSummary))

  // ── Completed → show the report ───────────────────────────────────────────
  // ⚠️ The amber block is rendered ABOVE it rather than instead of it. This used to be an early
  // return, so on a métré that HAS a report and a further uncovered passage the block was
  // unreachable — the drawer stayed calm while the Suivi board shouted about that passage.
  if (reportSummary) {
    return (
      <div className="mt-1 space-y-1.5" data-testid={`planning-${area}-report`}>
        {showMissing && <MissingReport
          area={area} reportSummary={reportSummary} eventDateLabel={eventDateLabel}
          onOpenReport={onOpenReport} readOnly={readOnly} unreceivedLines={unreceivedLines} />}
        {!open ? (
          <>
            <p className="truncate text-xs text-muted-foreground">{extract(reportSummary)}</p>
            <button
              type="button" onClick={() => setOpen(true)} data-testid={`planning-${area}-report-toggle`}
              className="text-xs font-medium text-muted-foreground hover:underline">
              Voir le rapport
            </button>
          </>
        ) : (
          <div className="space-y-1.5 rounded-md border bg-muted/30 p-2">
            <p className="whitespace-pre-wrap text-xs text-foreground">{reportSummary}</p>
            {reportSummaryAt && (
              /* SOLY-PLAN-006 MS-07 — « Rédigé le » became false at the first amendment:
                 ReportSummaryAt takes the date of the LAST save (décision D8, voie A). */
              <p className="text-[11px] text-muted-foreground">Mis à jour le {formatDate(reportSummaryAt)}</p>
            )}
            {/* Future attachments placeholder — disabled, non-interactive (MS-27 AC4). */}
            <div
              data-testid={`planning-${area}-report-documents`} aria-disabled="true"
              className="mt-1 flex items-center gap-2 rounded-md border border-dashed bg-muted/30 px-2 py-1.5 text-[11px] text-muted-foreground/70 select-none">
              <span aria-hidden="true">📎</span>
              <span>Documents — <span className="italic">bientôt</span></span>
            </div>
            {/* MÉTRÉ ONLY, and the asymmetry is the decision, not an oversight. On the pose the
                report CLOSES the pose (décision 4 de PLAN-003, not re-opened): a second constat
                there is a FINITION, with its own work item and its own report. Offering the
                gesture on the pose would also re-open a form nobody pre-fills, which is the very
                data loss this mini-scope exists to close. `area` is the discriminator this
                component already uses for its copy — no new grammar. */}
            {area === 'metre' && !readOnly && (
              <Button
                size="sm" variant="outline" className="w-full justify-start"
                data-testid="planning-metre-report-amend-cta" onClick={onOpenReport}>
                Compléter le rapport
              </Button>
            )}
            <button
              type="button" onClick={() => setOpen(false)} data-testid={`planning-${area}-report-toggle`}
              className="text-xs font-medium text-muted-foreground hover:underline">
              Masquer le rapport
            </button>
          </div>
        )}
      </div>
    )
  }

  // ── Missing report → phase « clôture » : un SEUL CTA primaire (mini-scope 29) ───────────────────
  if (showMissing) {
    return <MissingReport
      area={area} reportSummary={reportSummary} eventDateLabel={eventDateLabel}
      onOpenReport={onOpenReport} readOnly={readOnly} unreceivedLines={unreceivedLines} />
  }

  return null
}

/**
 * The amber « Rapport manquant » block — its own component since SOLY-PLAN-006 MS-04, because it is
 * no longer exclusive of the report view: a métré that HAS a report can still owe the constat of a
 * later passage, and then both are on screen at once.
 */
function MissingReport({
  area, reportSummary, eventDateLabel, onOpenReport, readOnly = false, unreceivedLines,
}: {
  area: 'metre' | 'pose'
  reportSummary: string | null
  eventDateLabel?: string
  onOpenReport: () => void
  readOnly?: boolean
  unreceivedLines?: { count: number; href: string }
}) {
  {
    const what = area === 'metre' ? 'Le métré' : 'La pose'
    // « La pose » is feminine: the pronoun cannot be hard-coded. It was wrong one time out of two,
    // and ALWAYS wrong on an intervention, which has no métré at all (QA staging 2026-08-20).
    const it = area === 'metre' ? 'le' : 'la'
    return (
      <div
        data-testid={`planning-${area}-report-missing`}
        className="mb-2 space-y-1.5 rounded-md border-l-2 border-amber-400 bg-amber-50/60 px-2 py-1.5">
        <p className="text-xs font-medium text-amber-700">Rapport manquant</p>
        <p className="text-[11px] text-amber-700/80">
          {reportSummary
            /* Un métré déjà rapporté dont un passage de PLUS est écoulé : « il reste à le clôturer »
               dirait faux — il l'a été, et le rapport est juste au-dessus. Ce qui manque, c'est le
               constat de ce passage-ci. */
            ? <>Un passage du métré est écoulé depuis le dernier rapport — il reste à le compléter.</>
            : <>{what}{eventDateLabel ? ` du ${eventDateLabel}` : ''} a eu lieu — il reste à {it} clôturer.</>}
        </p>
        {/* Le title va sur le wrapper, pas sur le Button : disabled:pointer-events-none le rendrait
            muet au survol. Voir DisabledHint. */}
        <DisabledHint
          className="block w-full"
          hint={readOnly ? PERMISSION_DENIED_HINT[PERMISSIONS.PLANNING_MANAGE] : undefined}>
          <Button
            size="sm" className="w-full justify-start"
            data-testid={`planning-${area}-report-missing-cta`} onClick={onOpenReport}
            disabled={readOnly}>
            {reportSummary
              ? 'Compléter le rapport'
              : area === 'metre' ? 'Terminer le métré + rapport' : 'Terminer la pose + rapport'}
          </Button>
        </DisabledHint>
        {/* Reconciliation reminder (SOLY-FINITION-001) — informational only, never blocks the report. */}
        {unreceivedLines && unreceivedLines.count > 0 && (
          <div data-testid="planning-report-unreceived-info" className="pt-0.5 text-[11px] text-amber-700/80">
            {unreceivedLines.count} ligne{unreceivedLines.count > 1 ? 's' : ''} fournisseur non reçue{unreceivedLines.count > 1 ? 's' : ''} sur
            ce projet — pensez à les clôturer.{' '}
            <Link to={unreceivedLines.href} data-testid="planning-report-unreceived-link" className="font-medium underline">
              Ouvrir le registre des commandes
            </Link>
          </div>
        )}
      </div>
    )
  }
}
